import React, { useState } from 'react';
import './ShieldEx.css';

const ShieldEx = ({ onSectionChange }) => {
  const [hoveredSection, setHoveredSection] = useState(null);

  const handleContactClick = () => {
    if (onSectionChange) {
      onSectionChange('contact');
    } else {
      const event = new CustomEvent('sectionChange', { detail: 'contact' });
      window.dispatchEvent(event);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const features = [
    {
      icon: 'fas fa-brain',
      title: 'AI-Powered Threat Detection',
      description: 'Advanced machine learning algorithms continuously analyze your environment to identify threats before they become incidents.'
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Risk Prioritization',
      description: 'Intelligently prioritize vulnerabilities based on real-world attack patterns and business impact, not just CVSS scores.'
    },
    {
      icon: 'fas fa-eye',
      title: 'Continuous Exposure Management',
      description: 'Continuously monitor and assess your attack surface, identifying and reducing exposure in real-time.'
    },
    {
      icon: 'fas fa-shield-virus',
      title: 'Attack Path Analysis',
      description: 'Understand how attackers actually exploit your environment by mapping attack paths and identifying critical vulnerabilities.'
    },
    {
      icon: 'fas fa-robot',
      title: 'GenAI-Enhanced Analytics',
      description: 'Leverage generative AI to provide intelligent insights, automated reporting, and contextual threat analysis.'
    },
    {
      icon: 'fas fa-bolt',
      title: 'Automated Response',
      description: 'Automated incident response and remediation workflows to reduce mean time to detection and response.'
    },
    {
      icon: 'fas fa-lock',
      title: 'Zero-Trust Architecture',
      description: 'Built on zero-trust principles, ensuring every access request is verified and validated.'
    },
    {
      icon: 'fas fa-network-wired',
      title: 'Real-time Monitoring',
      description: '24/7 real-time monitoring and alerting across your entire infrastructure, cloud, and hybrid environments.'
    }
  ];

  const benefits = [
    {
      title: 'Reduce Cyber Risk',
      description: 'Continuously identify and remediate vulnerabilities that attackers actually exploit, reducing your real-world cyber risk.'
    },
    {
      title: 'Prioritize Effectively',
      description: 'Focus on what matters most with AI-driven risk prioritization that considers attack paths and business impact.'
    },
    {
      title: 'Accelerate Response',
      description: 'Automated detection and response capabilities reduce mean time to detection (MTTD) and mean time to response (MTTR).'
    },
    {
      title: 'Gain Visibility',
      description: 'Complete visibility into your attack surface, exposure, and security posture across all environments.'
    }
  ];

  const useCases = [
    {
      title: 'Enterprise Security Posture',
      description: 'Comprehensive exposure management for large enterprises with complex, multi-cloud environments.'
    },
    {
      title: 'Compliance & Risk Management',
      description: 'Meet regulatory requirements and reduce cyber risk with continuous monitoring and automated reporting.'
    },
    {
      title: 'Threat Detection & Response',
      description: 'Advanced threat detection and automated incident response to protect against evolving cyber threats.'
    },
    {
      title: 'Security Operations',
      description: 'Empower your SOC team with AI-powered analytics, intelligent prioritization, and automated workflows.'
    }
  ];

  return (
    <>
      <div className="shieldex-hero-section">
        <div className="shieldex-hero-gradient"></div>
        <div className="container">
          <div className="shieldex-hero-content">
            <h1 className="shieldex-hero-title">ShieldEx</h1>
            <p className="shieldex-hero-description">
              An AI-powered cybersecurity exposure management platform that continuously identifies, prioritizes, and reduces real-world cyber risk by understanding how attackers actually exploit your environment.
            </p>
          </div>
          <div className="floating-wires">
            <svg className="wire-shield" viewBox="0 0 1200 400" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="shieldWireGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{stopColor: '#fbbf24', stopOpacity: 0.95}} />
                  <stop offset="50%" style={{stopColor: '#fcd34d', stopOpacity: 0.9}} />
                  <stop offset="100%" style={{stopColor: '#fde047', stopOpacity: 0.85}} />
                </linearGradient>
              </defs>
              {/* Multiple horizontal wires with floating curves - extended length */}
              {[...Array(30)].map((_, i) => {
                const y = 10 + (i * 12);
                const baseOffset = Math.sin(i * 0.3) * 10;
                const width = 1160;
                const x1 = 20;
                const x2 = x1 + width;
                const curve1 = 15 + Math.sin(i * 0.5) * 12;
                const curve2 = -12 + Math.cos(i * 0.4) * 10;
                const curve3 = 10 + Math.sin(i * 0.6) * 8;
                return (
                  <g key={i} className={`wire-horizontal wire-${i}`}>
                    <path 
                      d={`M ${x1} ${y + baseOffset} 
                          Q ${x1 + width/6} ${y + baseOffset - curve1} 
                          ${x1 + width/3} ${y + baseOffset} 
                          T ${x1 + width/2} ${y + baseOffset + curve2}
                          T ${x1 + width*2/3} ${y + baseOffset}
                          T ${x2} ${y + baseOffset - curve3}`}
                      fill="none" 
                      stroke="url(#shieldWireGrad)" 
                      strokeWidth="3" 
                      strokeLinecap="round"
                      opacity={0.9 - (i % 4) * 0.05}
                    />
                    <circle cx={x1} cy={y + baseOffset} r="2.5" fill="#fbbf24" opacity={0.95} />
                    <circle cx={x2} cy={y + baseOffset - curve3} r="2.5" fill="#fbbf24" opacity={0.95} />
                  </g>
                );
              })}
              {/* Vertical connecting wires with floating curves - extended length */}
              {[...Array(20)].map((_, i) => {
                const x = 20 + (i * 58);
                const y1 = 10;
                const y2 = 390;
                const midY = (y1 + y2) / 2;
                const baseOffset = Math.cos(i * 0.6) * 8;
                const curve1 = 20 + Math.sin(i * 0.5) * 15;
                const curve2 = -15 + Math.cos(i * 0.7) * 12;
                return (
                  <g key={`v-${i}`} className={`wire-vertical wire-v-${i}`}>
                    <path 
                      d={`M ${x + baseOffset} ${y1} 
                          Q ${x + baseOffset + curve1} ${midY - 40} 
                          ${x + baseOffset} ${midY - 20}
                          T ${x + baseOffset + curve2} ${midY}
                          T ${x + baseOffset} ${midY + 20}
                          T ${x + baseOffset} ${y2}`}
                      fill="none" 
                      stroke="url(#shieldWireGrad)" 
                      strokeWidth="2.5" 
                      strokeLinecap="round"
                      opacity={0.7}
                    />
                  </g>
                );
              })}
              {/* Intersection dots */}
              {[...Array(30)].map((_, hIndex) => {
                const hY = 10 + (hIndex * 12);
                const hBaseOffset = Math.sin(hIndex * 0.3) * 10;
                return [...Array(20)].map((_, vIndex) => {
                  const vX = 20 + (vIndex * 58);
                  const vBaseOffset = Math.cos(vIndex * 0.6) * 8;
                  // Approximate intersection point (where vertical wire crosses horizontal wire's y position)
                  const intersectX = vX + vBaseOffset;
                  const intersectY = hY + hBaseOffset;
                  return (
                    <circle 
                      key={`intersect-${hIndex}-${vIndex}`}
                      cx={intersectX} 
                      cy={intersectY} 
                      r="3" 
                      fill="#fbbf24" 
                      opacity={0.9}
                      className="intersection-dot"
                    />
                  );
                });
              })}
            </svg>
          </div>
        </div>
      </div>
      <section id="shieldex" className="shieldex-product">
        <div className="container">
          {/* Overview Section */}
          <div className="shieldex-overview">
            <div className="overview-content">
              <h2>Transform Your Cybersecurity Posture</h2>
              <p>
                ShieldEx is an AI-powered cybersecurity exposure management platform designed to help organizations 
                continuously identify, prioritize, and reduce real-world cyber risk. Unlike traditional vulnerability 
                management tools that rely on static CVSS scores, ShieldEx understands how attackers actually exploit 
                your environment, providing actionable intelligence to reduce your attack surface and prevent breaches.
              </p>
              <p>
                Our platform combines advanced machine learning, generative AI, and attack path analysis to deliver 
                a comprehensive view of your security posture, enabling you to focus on the vulnerabilities that matter most.
              </p>
            </div>
          </div>

          {/* Key Features */}
          <div className="shieldex-features">
            <div className="section-header">
              <h2 className="section-title">Key Features</h2>
            </div>
            <div className="features-circular-container">
              <div className="features-circle-wrapper">
                <svg className="circle-lines" viewBox="0 0 800 800" preserveAspectRatio="xMidYMid meet">
                  {/* Concentric circles */}
                  <circle cx="400" cy="400" r="280" fill="none" stroke="rgba(16, 185, 129, 0.2)" strokeWidth="2"/>
                  <circle cx="400" cy="400" r="224" fill="none" stroke="rgba(16, 185, 129, 0.15)" strokeWidth="1.5"/>
                  <circle cx="400" cy="400" r="168" fill="none" stroke="rgba(16, 185, 129, 0.1)" strokeWidth="1"/>
                  <circle cx="400" cy="400" r="112" fill="none" stroke="rgba(16, 185, 129, 0.1)" strokeWidth="1"/>
                  
                  {/* Radial lines dividing sections */}
                  {features.map((_, index) => {
                    const angle = (index * 360) / features.length - 90;
                    const x1 = 400;
                    const y1 = 400;
                    const x2 = 400 + Math.cos(angle * Math.PI / 180) * 280;
                    const y2 = 400 + Math.sin(angle * Math.PI / 180) * 280;
                    return (
                      <line
                        key={index}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke="rgba(16, 185, 129, 0.2)"
                        strokeWidth="1.5"
                      />
                    );
                  })}
                  
                  {/* Alternate section fills */}
                  {features.map((_, index) => {
                    const startAngle = (index * 360) / features.length - 90;
                    const endAngle = ((index + 1) * 360) / features.length - 90;
                    const innerRadius = 112;
                    const outerRadius = 280;
                    const isEven = index % 2 === 0;
                    
                    // Create path for section
                    const startXInner = 400 + Math.cos(startAngle * Math.PI / 180) * innerRadius;
                    const startYInner = 400 + Math.sin(startAngle * Math.PI / 180) * innerRadius;
                    const endXInner = 400 + Math.cos(endAngle * Math.PI / 180) * innerRadius;
                    const endYInner = 400 + Math.sin(endAngle * Math.PI / 180) * innerRadius;
                    
                    const startXOuter = 400 + Math.cos(startAngle * Math.PI / 180) * outerRadius;
                    const startYOuter = 400 + Math.sin(startAngle * Math.PI / 180) * outerRadius;
                    const endXOuter = 400 + Math.cos(endAngle * Math.PI / 180) * outerRadius;
                    const endYOuter = 400 + Math.sin(endAngle * Math.PI / 180) * outerRadius;
                    
                    const largeArcFlag = (endAngle - startAngle) > 180 ? 1 : 0;
                    
                    const pathD = `M ${startXInner} ${startYInner} 
                                   A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${endXInner} ${endYInner}
                                   L ${endXOuter} ${endYOuter}
                                   A ${outerRadius} ${outerRadius} 0 ${largeArcFlag} 0 ${startXOuter} ${startYOuter}
                                   Z`;
                    
                    return (
                      <path
                        key={`section-${index}`}
                        d={pathD}
                        fill={isEven ? "rgba(16, 185, 129, 0.08)" : "transparent"}
                        className="section-fill"
                        onMouseEnter={() => setHoveredSection(index)}
                        onMouseLeave={() => setHoveredSection(null)}
                        style={{ cursor: 'pointer' }}
                      />
                    );
                  })}
                  
                  {/* Curved text paths for each section */}
                  {features.map((feature, index) => {
                    const startAngle = (index * 360) / features.length - 90;
                    const endAngle = ((index + 1) * 360) / features.length - 90;
                    const radius = 240; // More space from icon (icon is at 280, text at 240 = 40px gap)
                    
                    // Create arc path for text that spans the entire section
                    const startX = 400 + Math.cos(startAngle * Math.PI / 180) * radius;
                    const startY = 400 + Math.sin(startAngle * Math.PI / 180) * radius;
                    const endX = 400 + Math.cos(endAngle * Math.PI / 180) * radius;
                    const endY = 400 + Math.sin(endAngle * Math.PI / 180) * radius;
                    const largeArcFlag = (endAngle - startAngle) > 180 ? 1 : 0;
                    
                    const pathId = `text-path-${index}`;
                    const pathD = `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`;
                    
                    // Split long titles into words for wrapping
                    // Check if title is long (3+ words OR character length > 25)
                    const words = feature.title.split(' ');
                    const titleLength = feature.title.length;
                    const isLongTitle = words.length >= 3 || titleLength > 25;
                    // Specifically check for known long titles
                    const longTitles = ['AI-Powered Threat Detection', 'Continuous Exposure Management'];
                    const shouldWrap = isLongTitle || longTitles.includes(feature.title);
                    const midPoint = Math.ceil(words.length / 2);
                    const line1 = words.slice(0, midPoint).join(' ');
                    const line2 = words.slice(midPoint).join(' ');
                    
                    // Create two paths for two-line text (slightly different radii)
                    const radius1 = radius - 8;
                    const radius2 = radius + 8;
                    const startX1 = 400 + Math.cos(startAngle * Math.PI / 180) * radius1;
                    const startY1 = 400 + Math.sin(startAngle * Math.PI / 180) * radius1;
                    const endX1 = 400 + Math.cos(endAngle * Math.PI / 180) * radius1;
                    const endY1 = 400 + Math.sin(endAngle * Math.PI / 180) * radius1;
                    const pathD1 = `M ${startX1} ${startY1} A ${radius1} ${radius1} 0 ${largeArcFlag} 1 ${endX1} ${endY1}`;
                    
                    const startX2 = 400 + Math.cos(startAngle * Math.PI / 180) * radius2;
                    const startY2 = 400 + Math.sin(startAngle * Math.PI / 180) * radius2;
                    const endX2 = 400 + Math.cos(endAngle * Math.PI / 180) * radius2;
                    const endY2 = 400 + Math.sin(endAngle * Math.PI / 180) * radius2;
                    const pathD2 = `M ${startX2} ${startY2} A ${radius2} ${radius2} 0 ${largeArcFlag} 1 ${endX2} ${endY2}`;
                    
                      return (
                        <g 
                          key={`text-${index}`} 
                          className={`curved-text-group-${index}`}
                          onMouseEnter={() => setHoveredSection(index)}
                          onMouseLeave={() => setHoveredSection(null)}
                          style={{ cursor: 'pointer' }}
                        >
                        <path id={pathId} d={pathD} fill="none" />
                        {shouldWrap ? (
                          // Two-line text for long titles (15% smaller font - 12px * 0.85 = 10.2px)
                          <>
                            <path id={`text-path-${index}-1`} d={pathD1} fill="none" />
                            <path id={`text-path-${index}-2`} d={pathD2} fill="none" />
                            <text
                              className={`curved-feature-text curved-text-${index}`}
                              fontSize="10.2"
                              fill="#1e293b"
                              fontWeight="700"
                            >
                              <textPath href={`#text-path-${index}-1`} startOffset="50%" textAnchor="middle">
                                {line1}
                              </textPath>
                            </text>
                            <text
                              className={`curved-feature-text curved-text-${index}`}
                              fontSize="10.2"
                              fill="#1e293b"
                              fontWeight="700"
                            >
                              <textPath href={`#text-path-${index}-2`} startOffset="50%" textAnchor="middle">
                                {line2}
                              </textPath>
                            </text>
                          </>
                        ) : (
                          // Single-line text for short titles
                          <text
                            className={`curved-feature-text curved-text-${index}`}
                            fontSize="14"
                            fill="#1e293b"
                            fontWeight="700"
                          >
                            <textPath href={`#${pathId}`} startOffset="50%" textAnchor="middle">
                              {feature.title}
                            </textPath>
                          </text>
                        )}
                      </g>
                    );
                  })}
                </svg>
                
                <div className="circle-center">
                  <div className="center-icon">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <h3>ShieldEx</h3>
                </div>
                
                {/* Tooltip */}
                {hoveredSection !== null && (
                  <div className="feature-tooltip">
                    <div className="tooltip-content">
                      <h4>{features[hoveredSection].title}</h4>
                      <p>{features[hoveredSection].description}</p>
                    </div>
                  </div>
                )}
                
                {features.map((feature, index) => {
                  const angle = (index * 360) / features.length - 90;
                  const sectionAngle = 360 / features.length;
                  
                  // Position icon outward from the edge to create space between icon and feature header
                  // Text is at 240px radius, so we'll position icons at 290px radius (50px gap)
                  // SVG viewBox is 800x800, center at (400, 400)
                  // Wrapper is 800px x 800px, so center is at 400px
                  const iconRadius = 290; // 50px gap from text at 240px
                  const headerAngle = angle + sectionAngle / 2;
                  const iconX = Math.cos(headerAngle * Math.PI / 180) * iconRadius;
                  const iconY = Math.sin(headerAngle * Math.PI / 180) * iconRadius;
                  
                  return (
                    <div 
                      key={index} 
                      className="feature-section"
                      data-section-index={index}
                      style={{
                        animationDelay: `${index * 0.1}s`
                      }}
                      onMouseEnter={() => setHoveredSection(index)}
                      onMouseLeave={() => setHoveredSection(null)}
                    >
                      {/* Icon on circumference */}
                      <div 
                        className="feature-section-icon-wrapper"
                        style={{
                          left: `calc(50% + ${iconX}px)`,
                          top: `calc(50% + ${iconY}px)`,
                          transform: 'translate(-50%, -50%)'
                        }}
                      >
                        <div className="feature-section-icon">
                          <i className={feature.icon}></i>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="shieldex-benefits">
            <div className="section-header">
              <h2 className="section-title">Key Benefits</h2>
              <p className="section-subtitle">Why organizations choose ShieldEx</p>
            </div>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <div key={index} className="benefit-card">
                  <div className="benefit-number">{index + 1}</div>
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Use Cases */}
          <div className="shieldex-use-cases">
            <div className="section-header">
              <h2 className="section-title">Use Cases</h2>
              <p className="section-subtitle">How ShieldEx addresses your security challenges</p>
            </div>
            <div className="use-cases-grid">
              {useCases.map((useCase, index) => (
                <div key={index} className="use-case-card">
                  <h3>{useCase.title}</h3>
                  <p>{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="shieldex-cta">
            <div className="cta-content">
              <h2>Ready to Transform Your Cybersecurity?</h2>
              <p>
                Schedule a demo to see how ShieldEx can help you reduce cyber risk, prioritize effectively, 
                and protect your organization from evolving threats.
              </p>
              <div className="cta-buttons">
                <button 
                  className="btn btn-primary"
                  onClick={handleContactClick}
                >
                  Request Demo
                  <i className="fas fa-arrow-right"></i>
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={handleContactClick}
                >
                  Contact Sales
                  <i className="fas fa-envelope"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ShieldEx;

